import { Resource } from '../interfaces/resource';
import {
    DraasApiDisk,
    DraasApiEnrollmentWorkflowRecommendation,
    DraasApiNetworkInterface,
    DraasApiVirtualMachine,
    DraasApiVmNetworkConfiguration,
} from '@pure/paas-api-gateway-client-ts';
import { DisasterRecoveryNetworkConfiguration } from './disaster-recovery-protection-group';

export class DisasterRecoveryVirtualMachine implements Resource {
    id: string;
    name: string;
    ephemeral: boolean;
    parentId: string;
    managedObjectReference?: string;
    powerState?: 'ON' | 'OFF' | 'SUSPENDED' | 'UNKNOWN';
    platform?: string;
    memoryMb: number;
    numOfCpus: number;
    ip?: string;
    agentVersion?: string;
    isTemplate: boolean;
    eligibility?: 'ELIGIBLE' | 'WARNING' | 'INELIGIBLE';
    ineligibilityReason?: string;
    vmwareToolsInstalled: boolean;
    vmwareToolsVersion?: string;
    disks?: DraasApiDisk[];
    diskTotalSize: number;
    networkInterfaces?: DraasApiNetworkInterface[];
    firmwareType?: string;
    instanceType?: string;
    displayProtectionGroupId?: string;
    homeProviderId?: string;
    managedInfrastructureVirtualMachineId?: string;
    currentDatacenterName?: string;
    currentHostSystemName?: string;
    protectionGroupId?: string;
    protectionGroupName?: string;
    deleted?: boolean;
    enrollmentStatus?:
        | 'ENROLL_PENDING'
        | 'ENROLLED'
        | 'ENROLL_FAILED'
        | 'REMOVAL_PENDING'
        | 'REMOVED'
        | 'REMOVAL_FAILED';
    unenrollDisabledReason?: 'NONE' | 'INGEST' | 'PROTECTION' | 'REPLICATION' | 'PRECONVERSION';
    tagNames: string[];
    // This field is populated only when requested through specific endpoint
    // GET /api/2.0/clusters/{clusterId}/recovery-plans/{planId}/protection-groups/{protectionGroupId}/vms
    networkConfiguration?: DisasterRecoveryVmNetworkConfiguration;
    protectionWorkflow: 'VADP' | 'FA_OFFLOAD' | 'FA_ASSIST';
    hasDatastoreWithFlashArray: boolean;
    // TODO XAAS-18578: Once this field is propagated all the way to production, remove the optional chaining
    recommendedProtectionWorkflows?: DisasterRecoveryProtectionWorkflowRecommendation[];

    constructor(json: DraasApiVirtualMachine) {
        this.id = json.id;
        this.name = json.name;
        this.ephemeral = json.ephemeral;
        this.parentId = json.parent_id;
        this.managedObjectReference = json.managed_object_reference;
        this.powerState = json.power_state;
        this.platform = json.platform;
        this.memoryMb = json.memory_mb;
        this.numOfCpus = json.num_of_cpus;
        this.ip = json.ip;
        this.agentVersion = json.agent_version;
        this.isTemplate = json.is_template;
        this.eligibility = json.eligibility;
        this.ineligibilityReason = json.ineligibility_reason;
        this.vmwareToolsInstalled = json.vmware_tools_installed;
        this.vmwareToolsVersion = json.vmware_tools_version;
        this.disks = json.disks;
        this.diskTotalSize = json.disk_total_size;
        this.networkInterfaces = json.network_interfaces;
        this.firmwareType = json.firmware_type;
        this.instanceType = json.instance_type;
        this.displayProtectionGroupId = json.display_protection_group_id;
        this.homeProviderId = json.home_provider_id;
        this.managedInfrastructureVirtualMachineId = json.managed_infrastructure_virtual_machine_id;
        this.currentDatacenterName = json.current_datacenter_name;
        this.currentHostSystemName = json.current_host_system_name;
        this.protectionGroupId = json.protection_group_id;
        this.protectionGroupName = json.protection_group_name;
        this.deleted = json.deleted;
        this.enrollmentStatus = json.enrollment_status;
        this.unenrollDisabledReason = json.unenroll_disabled_reason;
        this.tagNames = json.tag_names || [];
        this.networkConfiguration = json.network_configuration
            ? new DisasterRecoveryVmNetworkConfiguration(json.network_configuration)
            : null;
        this.protectionWorkflow = json.protection_workflow ?? 'VADP';
        this.hasDatastoreWithFlashArray = json.datastores?.some(ds => !!ds.flash_array) ?? false;
        // TODO XAAS-18578: null indicates that we will use old logic in UI
        // Nullable notation should be removed when this field is promoted to production in api-gw
        this.recommendedProtectionWorkflows =
            json.recommended_protection_workflows?.map(
                rpw => new DisasterRecoveryProtectionWorkflowRecommendation(rpw),
            ) ?? null;
    }

    isEnrolled(): boolean {
        return ['ENROLLED', 'ENROLL_PENDING', 'REMOVAL_PENDING', 'REMOVAL_FAILED'].includes(this.enrollmentStatus);
    }
}

export class DisasterRecoveryVmNetworkConfiguration {
    source?: DisasterRecoveryNetworkConfiguration;
    target?: DisasterRecoveryNetworkConfiguration;
    mapping?: DisasterRecoveryNetworkConfiguration;

    constructor(json: DraasApiVmNetworkConfiguration) {
        this.source = json.source ? new DisasterRecoveryNetworkConfiguration(json.source) : null;
        this.target = json.target ? new DisasterRecoveryNetworkConfiguration(json.target) : null;
        this.mapping = json.mapping ? new DisasterRecoveryNetworkConfiguration(json.mapping) : null;
    }
}

export class DisasterRecoveryProtectionWorkflowRecommendation {
    protectionWorkflow: 'VADP' | 'FA_OFFLOAD' | 'FA_ASSIST';
    isPreferred: boolean;
    isDisabled: boolean;
    note?: string;

    constructor(json: DraasApiEnrollmentWorkflowRecommendation) {
        this.protectionWorkflow = json.protection_workflow;
        this.isPreferred = json.is_preferred;
        this.isDisabled = json.is_disabled;
        this.note = json.note;
    }
}
